/**
 * Convert 24-hour time format to 12-hour AM/PM format
 * @param {string} timeString - Time in format "HH:00" (e.g., "19:00") or "Sum Of HH:00" (e.g., "Sum Of 19:00")
 * @returns {string} - Formatted time with AM/PM (e.g., "7 PM" or "Sum Of 7 PM")
 */
export const convertTo12HourFormat = (timeString) => {
    if (!timeString) return '';

    try {
        // Check if input contains "Sum Of" prefix
        const isSumOfFormat = timeString.includes('Sum Of');

        // Extract time from either "HH:00" or "Sum Of HH:00" format
        const timeMatch = timeString.match(/(\d{2}:\d{2})/);
        if (!timeMatch) {
            return timeString;
        }

        const [hours] = timeMatch[1].split(':').map(Number);

        if (hours < 0 || hours > 23) {
            return timeString;
        }

        let hour12 = hours % 12;
        const period = hours >= 12 ? 'PM' : 'AM';

        // 0 hours should display as 12
        if (hour12 === 0) {
            hour12 = 12;
        }

        const formatted12Hour = `${hour12} ${period}`;

        // Return only the formatted time without "Sum Of" prefix
        return formatted12Hour;
    } catch (error) {
        console.error('Error converting time format:', error);
        return timeString;
    }
};

/**
 * Format hourly sum header with converted time
 * @param {string} headerText - Header text like "Sum Of 00:00"
 * @returns {string} - Formatted header like "Sum Of 12 AM"
 */
export const formatHourlySumHeader = (headerText) => {
    if (!headerText) return '';

    // Extract time from "Sum Of HH:00" format
    const timeMatch = headerText.match(/(\d{2}:\d{2})/);
    if (!timeMatch) return headerText;

    const timeString = timeMatch[1];
    const converted12Hour = convertTo12HourFormat(timeString);

    return headerText.replace(timeString, converted12Hour);
};

/**
 * Convert 24-hour time format with seconds to 12-hour AM/PM format
 * @param {string} timeString - Time in format "HH:mm:ss" (e.g., "15:30:00", "00:30:00")
 * @returns {string} - Formatted time with AM/PM (e.g., "3:30 PM", "12:30 AM")
 */
export const convertTimeWithSecondsTo12Hour = (timeString) => {
    if (!timeString) return '';

    try {
        // Extract hours and minutes from "HH:mm:ss" format
        const timeMatch = timeString.match(/(\d{2}):(\d{2}):(\d{2})/);
        if (!timeMatch) {
            return timeString;
        }

        const hours = parseInt(timeMatch[1], 10);
        const minutes = timeMatch[2];

        if (hours < 0 || hours > 23) {
            return timeString;
        }

        let hour12 = hours % 12;
        const period = hours >= 12 ? 'PM' : 'AM';

        // 0 hours should display as 12
        if (hour12 === 0) {
            hour12 = 12;
        }

        // Include minutes if they are not "00"
        const formattedTime = minutes !== '00'
            ? `${hour12}:${minutes} ${period}`
            : `${hour12} ${period}`;

        return formattedTime;
    } catch (error) {
        console.error('Error converting time format:', error);
        return timeString;
    }
};

// input '1 AM'  ->  output '01:00 AM'
export const formatHourLabel = (input) => {
    if (!input) return '';

    const [hourStr, period] = input.trim().split(' ');
    let hour = parseInt(hourStr, 10);

    if (isNaN(hour) || !period) return input;

    const paddedHour = hour.toString().padStart(2, '0');

    return `${paddedHour}:00 ${period.toUpperCase()}`;
}